export interface HudDebugViewModel {
  readonly tick: number;
  readonly averageFps: number;
  readonly simulationHz: number;
  readonly playerX: number;
  readonly playerY: number;
  readonly playerZ: number;
  readonly streaming?: {
    readonly residentTiles: number;
    readonly requestedTiles: number;
    readonly visibleLod0Tiles: number;
    readonly cacheBytes: number;
    readonly evictions: number;
  };
  readonly locomotion?: {
    readonly mode: string;
    readonly surfaceId: string | null;
    readonly staticColliders: number;
    readonly contacts: number;
    readonly footstepSequence: number;
  };
  readonly environment?: {
    readonly season: string;
    readonly timeOfDayHours: number;
    readonly frozen: boolean;
    readonly audioResumed: boolean;
    readonly duskFactor: number;
    readonly nightFactor: number;
  };
}

export interface HudViewModel {
  readonly milestone: string;
  readonly placeLabel: string;
  readonly streetLabel: string | null;
  readonly statusLabel: string;
  readonly instructionsVisible: boolean;
  readonly presentationSettled: boolean;
  readonly debug: HudDebugViewModel | null;
}

export interface Hud {
  update(viewModel: HudViewModel): void;
  dispose(): void;
}

function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className: string
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  element.className = className;
  return element;
}

function formatMetric(value: number): string {
  return `${value.toFixed(2)} m`;
}

function formatBytes(value: number): string {
  if (value < 1_024) {
    return `${value} B`;
  }
  return `${(value / 1_024).toFixed(0)} KiB`;
}

export function readableSurfaceLabel(surfaceId: string | null): string {
  if (surfaceId === null) return "airborne";
  if (surfaceId.startsWith("building:")) return "built surface";
  if (surfaceId.startsWith("bridge:")) return "bridge deck";
  if (surfaceId.startsWith("road:")) return "street";
  if (
    surfaceId.startsWith("path:") ||
    surfaceId.startsWith("sidewalk:")
  ) {
    return "pavement";
  }
  if (surfaceId.startsWith("terrain:")) return "ground";
  return surfaceId.includes(":") ? "ground" : surfaceId;
}

export function formatLocomotionStatusLabel(
  mode: string,
  surfaceId: string | null,
): string {
  const movement = mode === "grounded" ? "on foot" : mode;
  return `${movement} · ${readableSurfaceLabel(surfaceId)}`;
}

function createKey(label: string): HTMLElement {
  const key = createElement("kbd", "hud-key");
  key.textContent = label;
  return key;
}

function createControl(label: string, description: string): HTMLLIElement {
  const item = createElement("li", "hud-control");
  item.append(createKey(label), document.createTextNode(description));
  return item;
}

export function createHud(root: HTMLElement): Hud {
  root.classList.add("game-ui");

  const hud = createElement("div", "vilnius-hud");

  const masthead = createElement("header", "hud-masthead");
  const eyebrow = createElement("p", "hud-eyebrow");
  eyebrow.textContent = "A field study";
  const title = createElement("h1", "hud-title");
  title.textContent = "Vilnius";
  const place = createElement("p", "hud-place");
  masthead.append(eyebrow, title, place);

  const status = createElement("output", "hud-status");
  status.setAttribute("aria-live", "polite");
  status.setAttribute("aria-atomic", "true");
  const statusMark = createElement("span", "hud-status-mark");
  statusMark.setAttribute("aria-hidden", "true");
  const statusText = createElement("span", "hud-status-text");
  const milestone = createElement("span", "hud-milestone");
  status.append(statusMark, statusText, milestone);

  const location = createElement("output", "hud-location");
  location.setAttribute("aria-live", "polite");
  location.setAttribute("aria-atomic", "true");
  location.hidden = true;
  const locationKicker = createElement("span", "hud-location-kicker");
  locationKicker.textContent = "Current street";
  const locationName = createElement("span", "hud-location-name");
  location.append(locationKicker, locationName);
  let previousStreetLabel: string | null = null;
  let locationRevision = 0;

  const reticle = createElement("div", "hud-reticle");
  reticle.setAttribute("aria-hidden", "true");

  const instructions = createElement("aside", "hud-instructions");
  instructions.setAttribute("aria-label", "Controls");
  const instructionHeading = createElement("h2", "hud-section-title");
  instructionHeading.textContent = "Traverse";
  const controls = createElement("ul", "hud-controls");
  controls.append(
    createControl("W A S D", "move"),
    createControl("Shift", "sprint"),
    createControl("Space", "jump"),
    createControl("Mouse", "look"),
    createControl("E", "interact"),
    createControl("L", "minimap"),
    createControl("M", "city map"),
    createControl("U", "appearance")
  );
  instructions.append(instructionHeading, controls);

  const debugToggle = createElement("button", "hud-debug-toggle");
  debugToggle.type = "button";
  debugToggle.hidden = true;
  debugToggle.setAttribute("aria-controls", "hud-debug-panel");
  debugToggle.setAttribute("aria-expanded", "false");
  debugToggle.append(
    document.createTextNode("Field notes"),
    createKey("F2"),
  );

  const debug = createElement("aside", "hud-debug");
  debug.id = "hud-debug-panel";
  debug.setAttribute("aria-label", "Runtime status");
  debug.hidden = true;
  const debugHeading = createElement("h2", "hud-section-title");
  debugHeading.textContent = "Field notes";
  const debugControlsHeading = createElement(
    "h3",
    "hud-section-title hud-field-notes-controls-title",
  );
  debugControlsHeading.textContent = "Traverse";
  const debugControls = createElement(
    "ul",
    "hud-controls hud-field-notes-controls",
  );
  debugControls.append(
    createControl("W A S D", "move"),
    createControl("Shift", "sprint"),
    createControl("Space", "jump"),
    createControl("Mouse", "look"),
    createControl("E", "interact"),
    createControl("L", "map"),
  );
  const debugList = createElement("dl", "hud-debug-list");

  const tickLabel = createElement("dt", "");
  tickLabel.textContent = "Tick";
  const tickValue = createElement("dd", "");
  const fpsLabel = createElement("dt", "");
  fpsLabel.textContent = "Frame";
  const fpsValue = createElement("dd", "");
  const simLabel = createElement("dt", "");
  simLabel.textContent = "Step";
  const simValue = createElement("dd", "");
  const positionLabel = createElement("dt", "");
  positionLabel.textContent = "Local";
  const positionValue = createElement("dd", "hud-position");
  const tileLabel = createElement("dt", "hud-streaming-row");
  tileLabel.textContent = "Tiles";
  const tileValue = createElement("dd", "hud-streaming-row");
  tileValue.dataset.streamingMetric = "tiles";
  const lodLabel = createElement("dt", "hud-streaming-row");
  lodLabel.textContent = "Near LOD";
  const lodValue = createElement("dd", "hud-streaming-row");
  lodValue.dataset.streamingMetric = "lod";
  const cacheLabel = createElement("dt", "hud-streaming-row");
  cacheLabel.textContent = "Cache";
  const cacheValue = createElement("dd", "hud-streaming-row");
  cacheValue.dataset.streamingMetric = "cache";
  const motionLabel = createElement("dt", "hud-locomotion-row");
  motionLabel.textContent = "Motion";
  const motionValue = createElement("dd", "hud-locomotion-row");
  motionValue.dataset.locomotionMetric = "motion";
  const surfaceLabel = createElement("dt", "hud-locomotion-row");
  surfaceLabel.textContent = "Surface";
  const surfaceValue = createElement("dd", "hud-locomotion-row");
  surfaceValue.dataset.locomotionMetric = "surface";
  const physicsLabel = createElement("dt", "hud-locomotion-row");
  physicsLabel.textContent = "Physics";
  const physicsValue = createElement("dd", "hud-locomotion-row");
  physicsValue.dataset.locomotionMetric = "physics";
  const atmosphereLabel = createElement("dt", "hud-environment-row");
  atmosphereLabel.textContent = "Atmosphere";
  const atmosphereValue = createElement("dd", "hud-environment-row");
  atmosphereValue.dataset.environmentMetric = "atmosphere";
  const soundLabel = createElement("dt", "hud-environment-row");
  soundLabel.textContent = "Sound";
  const soundValue = createElement("dd", "hud-environment-row");
  soundValue.dataset.environmentMetric = "sound";

  debugList.append(
    tickLabel,
    tickValue,
    fpsLabel,
    fpsValue,
    simLabel,
    simValue,
    positionLabel,
    positionValue,
    tileLabel,
    tileValue,
    lodLabel,
    lodValue,
    cacheLabel,
    cacheValue,
    motionLabel,
    motionValue,
    surfaceLabel,
    surfaceValue,
    physicsLabel,
    physicsValue,
    atmosphereLabel,
    atmosphereValue,
    soundLabel,
    soundValue
  );
  debug.append(
    debugHeading,
    debugControlsHeading,
    debugControls,
    debugList,
  );

  hud.append(
    masthead,
    status,
    location,
    reticle,
    instructions,
    debugToggle,
    debug,
  );
  root.replaceChildren(hud);

  let diagnosticsVisible = false;
  let debugAvailable = false;

  function setDiagnosticsVisible(visible: boolean): void {
    diagnosticsVisible = visible && debugAvailable;
    debug.hidden = !diagnosticsVisible;
    debugToggle.setAttribute(
      "aria-expanded",
      String(diagnosticsVisible),
    );
    debugToggle.dataset.open = String(diagnosticsVisible);
  }

  function onDebugToggleClick(): void {
    setDiagnosticsVisible(!diagnosticsVisible);
  }

  function onGlobalKeyDown(event: KeyboardEvent): void {
    if (event.code !== "F2" || !debugAvailable) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    setDiagnosticsVisible(!diagnosticsVisible);
  }

  debugToggle.addEventListener("click", onDebugToggleClick);
  window.addEventListener("keydown", onGlobalKeyDown, { capture: true });

  return {
    update(viewModel: HudViewModel): void {
      place.textContent = viewModel.placeLabel;
      statusText.textContent = viewModel.statusLabel;
      milestone.textContent = viewModel.milestone;
      const presentation = viewModel.presentationSettled
        ? "settled"
        : "arrival";
      hud.dataset.presentation = presentation;
      root.dataset.presentation = presentation;
      const streetLabel = viewModel.streetLabel?.trim() || null;
      if (streetLabel !== previousStreetLabel) {
        previousStreetLabel = streetLabel;
        location.hidden = streetLabel === null;
        if (streetLabel !== null) {
          locationName.textContent = streetLabel;
          locationRevision += 1;
          location.dataset.revision =
            locationRevision % 2 === 0 ? "even" : "odd";
        }
      }
      instructions.hidden =
        !viewModel.instructionsVisible || viewModel.presentationSettled;
      debugAvailable = viewModel.debug !== null;
      debugToggle.hidden = !debugAvailable;
      debug.hidden = !debugAvailable || !diagnosticsVisible;

      if (viewModel.debug !== null) {
        tickValue.textContent = viewModel.debug.tick.toLocaleString("en-GB");
        fpsValue.textContent = `${viewModel.debug.averageFps.toFixed(1)} fps`;
        simValue.textContent = `${viewModel.debug.simulationHz.toFixed(0)} Hz`;
        positionValue.textContent = [
          formatMetric(viewModel.debug.playerX),
          formatMetric(viewModel.debug.playerY),
          formatMetric(viewModel.debug.playerZ)
        ].join("  ·  ");
        const streaming = viewModel.debug.streaming;
        for (const element of [
          tileLabel,
          tileValue,
          lodLabel,
          lodValue,
          cacheLabel,
          cacheValue
        ]) {
          element.hidden = streaming === undefined;
        }
        if (streaming !== undefined) {
          tileValue.textContent =
            `${streaming.residentTiles} resident · ${streaming.requestedTiles} queued`;
          lodValue.textContent = `${streaming.visibleLod0Tiles} tiles`;
          cacheValue.textContent =
            `${formatBytes(streaming.cacheBytes)} · ${streaming.evictions} evicted`;
        }
        const locomotion = viewModel.debug.locomotion;
        for (const element of [
          motionLabel,
          motionValue,
          surfaceLabel,
          surfaceValue,
          physicsLabel,
          physicsValue
        ]) {
          element.hidden = locomotion === undefined;
        }
        if (locomotion !== undefined) {
          motionValue.textContent =
            `${locomotion.mode} · ${locomotion.contacts} contacts`;
          surfaceValue.textContent = locomotion.surfaceId ?? "airborne";
          physicsValue.textContent =
            `${locomotion.staticColliders} static · step ${locomotion.footstepSequence}`;
        }
      const environment = viewModel.debug.environment;
      hud.dataset.tone =
        environment !== undefined &&
          (environment.duskFactor >= 0.7 || environment.nightFactor >= 0.35)
          ? "dark"
          : "light";
        for (const element of [
          atmosphereLabel,
          atmosphereValue,
          soundLabel,
          soundValue,
        ]) {
          element.hidden = environment === undefined;
        }
        if (environment !== undefined) {
          const hour = environment.timeOfDayHours;
          const hours = Math.floor(hour);
          const minutes = Math.floor((hour - hours) * 60);
          atmosphereValue.textContent =
            `${environment.season} · ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} · ${environment.frozen ? "held" : "running"}`;
          soundValue.textContent = environment.audioResumed
            ? "procedural mix active"
            : "click to enable";
        }
      }
    },

    dispose(): void {
      window.removeEventListener("keydown", onGlobalKeyDown, {
        capture: true,
      });
      debugToggle.removeEventListener("click", onDebugToggleClick);
      root.replaceChildren();
      root.classList.remove("game-ui");
      delete root.dataset.presentation;
    }
  };
}
